import React from 'react';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import { useDispatch } from 'react-redux';
import {
  useAdvanceBoolean,
  useBoolean,
  useGetCompanySsoConfigList,
  useWealthlaneCustomerApi,
} from '../../hooks';
import Button from '../../components/button';
import { CompanySsoHeader } from '../../constants/table-headers/companySsoHeader';
import { PageHeader } from '../settings/company-profile/profile.styled';
import Heading from '../../components/typography';
import SsoConfigurationModal from './ssoConfigurationModal';
import UpdateSigningCertificateModal from './signingCertificateModal';
import usePostEnableDisableSso from '../../hooks/post/usePostEnableDisableSso';
import ToastComponent from '../../components/toast';
import AnimationWrapper from '../../components/animation/animationWrapper';
import AlertPopup from '../../components/alert-popup';
import { toast } from 'react-toastify';
import Spinners from '../../components/spinner';
import { axiosInstance } from '../../services/apiInterceptor';

enum AlertStatus {
  ENABLE = 'enable',
  DISABLE = 'disable',
}

// const schemaData = {
//   columns: [
//     {
//       Header: 'Provider Name',
//       accessor: 'providerName',
//       width: 200,
//       sortable: true,
//       isSubHeader: false,
//     },
//     // {
//     //   Header: 'Provider Code',
//     //   accessor: 'providerCode',
//     //   width: 150,
//     //   sortable: true,
//     //   isSubHeader: false,
//     // },
//     {
//       Header: 'Client ID',
//       accessor: 'clientId',
//       width: 200,
//       sortable: true,
//       isSubHeader: false,
//     },
//     {
//       Header: 'Authority',
//       accessor: 'authority',
//       width: 250,
//       sortable: true,
//       isSubHeader: false,
//     },
//     {
//       Header: 'Redirect URI',
//       accessor: 'redirectUri',
//       width: 250,
//       sortable: true,
//       isSubHeader: false,
//     },
//     {
//       Header: 'Status',
//       accessor: 'isEnabled',
//       width: 120,
//       center: true,
//       cellCenter: true,
//       isBadgeField: true,
//       sortable: false,
//       badgeStatus: {
//         true: 'success',
//         false: 'error',
//       },
//       conditionalValue: {
//         true: 'Enabled',
//         false: 'Disabled',
//       },
//       isSubHeader: false,
//     },
//     {
//       Header: 'Created Date',
//       accessor: 'createdDate',
//       width: 150,
//       sortable: true,
//       isDateFieldWithTime: true,
//       isSubHeader: false,
//     },
//   ],
//   options: {
//     showToolbar: true,
//     showImportBlock: false,
//     showPagination: true,
//     showFilter: false,
//     showSearch: true,
//     filters: [],
//   },
// };

const SSOConfiguration = () => {
  const dispatch = useDispatch();
  const { handleRequest } = useWealthlaneCustomerApi();
  const { tableInfo, setTableInfo } = useDynamicTable();

  const {
    value: isSsoModalOpen,
    setFalse: closeSsoModal,
    setTrue: openSsoModal,
    customValue: ssoObject,
    setCustomValue: setSsoObject,
  } = useAdvanceBoolean<string>(false);

  const {
    value: isAlertOpen,
    setFalse: closeAlert,
    setTrue: openAlert,
    customValue: alertStatus,
    setCustomValue: setAlertStatus,
  } = useAdvanceBoolean<string>(false);

  const {
    value: isSigningCertificateModalOpen,
    setFalse: closeSigningCertificateModal,
    setTrue: openSigningCertificateModal,
  } = useBoolean(false);

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'COMPANY_SSO_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });

  const {
    data: companySsoConfigList,
    isLoading,
    status,
  } = useGetCompanySsoConfigList(handleRequest, tableInfo);

  const { mutateAsync: postEnableDisableSso, isLoading: isLoadingPostEnableDisableSso } =
    usePostEnableDisableSso(handleRequest, () => {
      toast.success(
        <ToastComponent
          label='Success'
          message={`SSO Configuration ${alertStatus}d successfully.`}
        />
      );
    });

  const data = React.useMemo(
    () => companySsoConfigList?.items || [],
    [companySsoConfigList, isLoading]
  );

  const actions = [
    {
      action: 'View',
      as: 'View',
      isEnabled: (rowData: any) => true,
      onClick: (id: string, rowData: any) => {
        setSsoObject((data: any) => ({ ...data, rowData, selectedId: id, modalStatus: 'View' }));
        openSsoModal();
      },
    },
    {
      action: 'Edit',
      as: 'Edit',
      isEnabled: (rowData: any) => true,
      onClick: (id: string, rowData: any) => {
        setSsoObject((data: any) => ({ ...data, rowData, selectedId: id, modalStatus: 'Edit' }));
        openSsoModal();
      },
    },
    {
      action: 'Enable',
      as: 'Edit',
      isEnabled: (rowData: any) => !rowData.isEnabled,
      onClick: (id: string, rowData: any) => {
        setAlertStatus('enable');
        setSsoObject((data: any) => ({ ...data, rowData }));
        openAlert();
      },
    },
    {
      action: 'Disable',
      as: 'Edit',
      isEnabled: (rowData: any) => rowData.isEnabled,
      onClick: (id: string, rowData: any) => {
        setAlertStatus('disable');
        setSsoObject((data: any) => ({ ...data, rowData }));
        openAlert();
      },
    },
    {
      action: 'Update Signing Certificate',
      as: 'Edit',
      isEnabled: (rowData: any) => rowData?.providerType === 'SAML',
      onClick: (id: string, rowData: any) => {
        setSsoObject((data: any) => ({
          ...data,
          selectedId: id,
        }));
        openSigningCertificateModal();
      },
    },
  ];

  const onEnableDisableSso = async (): Promise<void> => {
    if (AlertStatus.ENABLE === alertStatus) {
      await postEnableDisableSso({ id: ssoObject?.rowData?.id, isEnabled: true });
    } else {
      await postEnableDisableSso({ id: ssoObject?.rowData?.id, isEnabled: false });
    }
    closeAlert();
  };

  const renderRightToolbarButton = () => {
    return (
      <Button
        as='Create'
        variant={'primary'}
        title={'Add SSO Configuration'}
        onClick={() => {
          setSsoObject((data: any) => ({ ...data, modalStatus: 'Add' }));
          openSsoModal();
        }}
        icon='add_circle'
      />
    );
  };

  const schema = React.useMemo(
    () => ({
      columns: CompanySsoHeader({ actions: schemaData?.columns, tableActions: actions }),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );

  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  return (
    <div>
      <PageHeader>
        <Heading variant='h3' title='SSO Configuration' />
        <Heading variant='body-s' title='List of SSO Configuration.' />
      </PageHeader>

      <DynamicTable
        schema={schema}
        data={data}
        totalCounts={companySsoConfigList?.totalCount ?? 10}
        loading={status}
        components={{
          renderRightToolbarButton,
        }}
      />

      <AnimationWrapper>
        {isSsoModalOpen && (
          <SsoConfigurationModal
            id={ssoObject?.selectedId}
            rowData={ssoObject?.rowData}
            isModalOpen={isSsoModalOpen}
            modalStatus={ssoObject?.modalStatus}
            closeModal={() => {
              closeSsoModal();
              setSsoObject((data: any) => ({ ...data, selectedId: null }));
            }}
            onEditModal={() => {
              setSsoObject((data: any) => ({ ...data, modalStatus: 'Edit' }));
            }}
            handleRequest={handleRequest}
          />
        )}
        {isSigningCertificateModalOpen && (
          <UpdateSigningCertificateModal
            id={ssoObject?.selectedId}
            isModalOpen={isSigningCertificateModalOpen}
            modalStatus={ssoObject?.modalStatus}
            closeModal={() => {
              closeSigningCertificateModal();
              setSsoObject((data: any) => ({ ...data, selectedId: null }));
            }}
          />
        )}
        {isAlertOpen && (
          <AlertPopup
            isShow={isAlertOpen}
            closeModal={closeAlert}
            customTitle={`Are you sure you want to ${alertStatus} this config ?`}
            handleSubmit={onEnableDisableSso}
            loading={isLoadingPostEnableDisableSso}
          />
        )}
      </AnimationWrapper>
    </div>
  );
};

export default withDynamicTableContext(SSOConfiguration, false, axiosInstance);
